import {AppUrls} from 'shared/config/model/urls';
import React from 'react';
import {useSelector} from 'react-redux';
import {t} from '@lingui/core/macro';

import useOpenState from 'shared/lib/hooks/useOpenState';
import useListParams from 'shared/lib/hooks/useListParams';
import useResetPageOnEmpty from 'shared/lib/hooks/useResetPageOnEmpty';

import {statusPageAPI} from 'features/statusPage/api/statusPageAPI';
import {STATUS_PAGE_FILTER_KEYS} from 'features/statusPage/lib/statusPageFilters';
import {IColumn} from 'shared/ui/Table/Table';
import ListPage from 'shared/ui/ListPage/ListPage';
import ListToolbar from 'shared/ui/ListToolbar/ListToolbar';
import TableToolbar from 'shared/ui/TableToolbar/TableToolbar';
import ListFiltersModal from 'shared/ui/Modal/ListFiltersModal/ListFiltersModal';
import {
    CreateGatedButton,
    ExceededModal,
    userSelector,
    usePlugState,
    useHasTariffLimit,
    useIsUnderUserLimit,
} from 'entities/user';
import {useCreateGate} from 'entities/tariff';
import PlugEmpty from 'shared/ui/Plugs/PlugEmpty/PlugEmpty';
import PlugNoAccess from 'shared/ui/Plugs/PlugNoAccess/PlugNoAccess';

import StatusPageRow from '../StatusPageRow/StatusPageRow';
import StatusPageModal from '../StatusPageModal/StatusPageModal';
import StatusPagesFiltersForm from '../StatusPagesFiltersForm/StatusPagesFiltersForm';

const StatusPages: React.FC = () => {
    const {args, sort, setSort, hasFilters, search, onSearch} = useListParams({
        filterKeys: STATUS_PAGE_FILTER_KEYS,
        defaultSort: {field: 'id', direction: 'desc'},
    });
    const filtersModal = useOpenState();

    const user = useSelector(userSelector);
    const hasAccess = useHasTariffLimit('status_pages_max_count_limit');
    const canCreate = useIsUnderUserLimit('status_pages_max_count_limit', 'status_pages_count');
    const {open, close, isCreateOpen, isExceededOpen} = useCreateGate(canCreate);

    const limit = user?.tariff?.status_pages_max_count_limit ?? 0;
    const count = user?.status_pages_count ?? 0;

    const {data: pagination, isLoading, isFetching, refetch} =
        statusPageAPI.useGetStatusPagesQuery(args, {skip: !hasAccess});

    const isRedirecting = useResetPageOnEmpty(pagination);

    const plugState = usePlugState({
        tariffField: 'status_pages_max_count_limit',
        isLoading: isLoading || isRedirecting,
        isEmpty: pagination?.items?.length === 0 && pagination?.totalItems === 0,
        hasFilters,
    });

    const columns: IColumn[] = [
        {label: t`Название`, sort: 'name'},
        {label: t`URL`, sort: 'slug'},
        {label: t`Кол-во мониторов`},
        {label: t`Баннеры отключены`},
        {label: t`Дата создания`, sort: 'created_at'},
        {label: t`Статус`, sort: 'is_active'},
        {label: ''},
    ];

    return (
        <ListPage
            documentTitle={t`Страницы статуса | Overseer`}
            toolbar={
                <ListToolbar
                    title={t`Страницы статуса`}
                    backUrl={AppUrls.HOME}
                    count={limit > 0 ? count : undefined}
                    maxCount={limit > 0 ? limit : undefined}
                    createButton={
                        <CreateGatedButton
                            label={t`Создать страницу`}
                            canCreate={canCreate}
                            exceededType="status_pages"
                            Modal={StatusPageModal}
                        />
                    }
                    hideCreateButtonOnMobile={plugState === 'noAccess' || plugState === 'empty'}
                    actions={
                        <TableToolbar
                            onSearch={onSearch}
                            searchValue={search}
                            onFilter={filtersModal.open}
                            onRefresh={refetch}
                            isRefreshing={isFetching}
                        />
                    }
                />
            }
            plug={
                <>
                    {plugState === 'noAccess' && <PlugNoAccess/>}
                    {plugState === 'empty' && <PlugEmpty onCreate={open} buttonText={t`Создать страницу`}/>}
                </>
            }
            columns={columns}
            items={pagination?.items}
            rowFactory={(item) =>
                <StatusPageRow
                    key={item.uuid}
                    item={item}
                    user={user}
                    canCreate={canCreate}
                />
            }
            isLoading={isLoading || isRedirecting}
            pagination={pagination}
            sort={sort}
            setSort={setSort}
            emptyStateComponent={plugState === 'notFound' ? t`Ничего не найдено` : true}
        >
            <ExceededModal isOpen={isExceededOpen} handleClose={close} type="status_pages"/>
            <StatusPageModal open={isCreateOpen} handleClose={close}/>
            <ListFiltersModal
                open={filtersModal.isOpen}
                handleClose={filtersModal.close}
                Form={StatusPagesFiltersForm}
            />
        </ListPage>
    );
};

export default StatusPages;
